import React from 'react';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { AdvancedSearch, AISearch } from 'common';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { API_CLIENT_NAMES } from 'services/API';
import { FilterType } from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';

import { CityFilter } from '../CityFilter';
import { FILTER_CONFIG, FILTER_GROUPS, PINNED_FILTER_NAME } from '../Filters/Filters.constants';
import { useFiltersStyles } from '../Filters/Filters.styled';
import { SalaryFilter } from '../SalaryFilter';
import { TextFilter } from '../TextFilter';

const DEFAULT_TYPEAHEAD_PARAMS = { limitKey: 'size' };

export const FiltersList = ({ formValues, onFilterChange }) => {
  const classes = useFiltersStyles();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const renderFilter = filterConfig => {
    const {
      apiClient,
      coordinatesFieldName,
      defaultPriority,
      endpoint,
      fieldName,
      formatSelectedItems,
      formatValue,
      getOptionId,
      getOptionLabel,
      getValue,
      groupBy,
      isSingleSelect,
      name,
      placeholder,
      preferencesApiClient,
      preferencesEndpoint,
      preferencesFieldName,
      renderOption,
      salaryTypeFieldName,
      title,
      transform,
      type,
      typeahead,
      typeaheadLimit,
      typeaheadParams
    } = filterConfig;

    const filterValue = formValues?.[name]?.value;

    if (type === FilterType.AdvancedSearch) {
      return (
        <>
          <AdvancedSearch
            apiClient={apiClient ?? API_CLIENT_NAMES.Secondary}
            defaultPriority={defaultPriority}
            endpoint={endpoint}
            fieldName={fieldName}
            getOptionId={getOptionId || (option => option.id ?? option.value ?? option)}
            getOptionLabel={getOptionLabel || (option => option.value)}
            groupBy={groupBy}
            onSelectionChange={(fieldNameVal, selectedItems) => {
              onFilterChange(name, {
                fieldName: fieldNameVal ?? formValues?.[name]?.fieldName,
                preferencesFieldName,
                value: formatSelectedItems ? formatSelectedItems(selectedItems) : selectedItems
              });
            }}
            placeholder={placeholder}
            preferencesApiClient={preferencesApiClient}
            preferencesEndpoint={preferencesEndpoint}
            renderOption={renderOption}
            selectedItems={filterValue || []}
            isSingleSelect={isSingleSelect}
            title={title}
            typeahead={typeahead}
            typeaheadLimit={typeaheadLimit}
            typeaheadParams={typeaheadParams ?? DEFAULT_TYPEAHEAD_PARAMS}
          />
          <Divider />
        </>
      );
    }

    if (type === FilterType.Search) {
      return (
        <>
          <AISearch
            title={title}
            placeholder={placeholder}
            value={getValue ? getValue(filterValue) : filterValue}
            onChange={(nameVal, value) => {
              onFilterChange(nameVal, {
                fieldName,
                value: formatValue ? formatValue(value) : value
              });
            }}
            name={name}
            showAccuracySettings={checkIfFeatureFlagEnabled(FeatureFlags.CandidatesSearchAccuracy)}
          />
          <Divider />
        </>
      );
    }

    if (type === FilterType.City) {
      return (
        <>
          <CityFilter
            coordinatesFieldName={coordinatesFieldName}
            endpoint={endpoint}
            fieldName={fieldName}
            groupBy={groupBy}
            name={name}
            title={title}
            value={filterValue}
            onChange={onFilterChange}
          />
          <Divider />
        </>
      );
    }

    if (type === FilterType.Text) {
      return (
        <>
          <TextFilter
            fieldName={fieldName}
            name={name}
            placeholder={placeholder}
            title={title}
            value={filterValue}
            onChange={onFilterChange}
            transform={transform}
          />
          <Divider />
        </>
      );
    }

    if (type === FilterType.Salary) {
      return (
        <>
          <SalaryFilter
            endpoint={endpoint}
            fieldName={fieldName}
            name={name}
            salaryTypeFieldName={salaryTypeFieldName}
            title={title}
            value={filterValue}
            onChange={onFilterChange}
          />
          <Divider />
        </>
      );
    }

    return null;
  };

  const byName = Object.fromEntries(FILTER_CONFIG.map(cfg => [cfg.name, cfg]));

  const groupedFilterNames = new Set([
    PINNED_FILTER_NAME,
    ...FILTER_GROUPS.flatMap(g => g.filters)
  ]);

  const ungroupedConfigs = FILTER_CONFIG.filter(
    cfg => !groupedFilterNames.has(cfg.name) && !cfg.isDateRange
  );

  return (
    <>
      {byName[PINNED_FILTER_NAME] && (
        <React.Fragment key={PINNED_FILTER_NAME}>
          {renderFilter(byName[PINNED_FILTER_NAME])}
        </React.Fragment>
      )}

      {FILTER_GROUPS.map(group => (
        <section key={group.id} className={classes.group}>
          <Typography variant="subtitle2" className={classes.groupTitle}>
            {group.name}
          </Typography>
          {group.filters.map(filterName =>
            byName[filterName] ? (
              <React.Fragment key={filterName}>{renderFilter(byName[filterName])}</React.Fragment>
            ) : null
          )}
        </section>
      ))}

      {ungroupedConfigs.length > 0 && (
        <section className={classes.group}>
          {ungroupedConfigs.map(cfg => (
            <React.Fragment key={cfg.name}>{renderFilter(cfg)}</React.Fragment>
          ))}
        </section>
      )}
    </>
  );
};
